import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import { CONTACT } from "@/data/content";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Industries", href: "#industries" },
  { label: "Why ACI", href: "#why-aci" },
  { label: "Contact", href: "#quote" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled
          ? "glass border-b border-line/10 shadow-[0_8px_32px_rgb(var(--c-ink)/0.12)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-2 md:px-12">
        <a href="#top" data-testid="header-logo-link" aria-label="ACI home" className="flex flex-col items-start gap-1">
          <span className="flex items-center gap-4">
            <span className="logo-chip flex items-center">
              <img
                src="/aci-logo.png"
                alt="ACI Automation Control Instrumentation logo"
                className="h-14 w-auto sm:h-16"
                loading="eager"
              />
            </span>
            <span aria-hidden="true" className="hidden h-9 w-px bg-line/15 sm:block" />
            <span className="logo-chip flex items-center">
              <img
                src="/cl-logo.png"
                alt="Caldwell Levesque logo"
                data-testid="header-cl-logo"
                className="h-8 w-auto sm:h-9"
                loading="eager"
              />
            </span>
          </span>
          <span data-testid="header-partner-line" className="hud-label hidden lg:block">
            ACI — in partnership with Caldwell Levesque
          </span>
        </a>
        <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono text-xs uppercase tracking-[0.18em] text-body-strong transition-colors duration-200 hover:text-brand"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Button
            asChild
            data-testid="header-quote-button"
            className="rounded-full bg-brand px-5 font-semibold text-base shadow-[0_0_18px_rgb(var(--c-brand)/0.35)] transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-hi hover:shadow-[0_0_28px_rgb(var(--c-brand)/0.55)]"
          >
            <a href="#quote">Request a quote</a>
          </Button>
        </div>
      </div>
      <motion.div
        aria-hidden="true"
        data-testid="scroll-progress-bar"
        style={{ scaleX: progress }}
        className="h-[2px] origin-left bg-brand shadow-[0_0_10px_rgb(var(--c-brand)/0.8)]"
      />
    </header>
  );
}
